/**
 * ShiftDashboard.jsx
 * CCH Care — Phase 2 + 2.5 + 3
 * Last Modified: 2026-05-13
 *
 * Main container for the shift dashboard view.
 * Shows SetupWizard when facility setup is incomplete (admin only).
 * Orchestrates: ShiftSummaryBar, FilterToolbar, ResidentList, ResidentDetail
 *
 * Changes (2026-05-13) — Phase 2 (Shift-Based Access Control):
 * - GraceWindowBanner mounts at the top of the active-shift render
 *   when useShift().inGraceWindow is true. The dashboard route is not
 *   shift-gated (it's the redirect target for RequireOnShift), so the
 *   banner mounts here directly rather than via the guard. Per Policy
 *   v1.0 §4.3.
 *
 * Changes (2026-04-22, late):
 * - ShiftSummaryBar now receives isFloatView so it can render the
 *   "Overseeing" pill in oversight mode.
 * - EmptyState for empty-resident-list chooses between no_residents
 *   (rostered CNA waiting on assignment) and no_residents_float
 *   (admin overseeing a shift with no CNAs/residents yet) based on
 *   shift.is_float_view.
 *
 * Changes (2026-04-22, earlier):
 * - Setup gate now reads `is_dashboard_ready` instead of `is_setup_complete`.
 *   The old gate redirected admins back to the wizard whenever ANY step was
 *   incomplete — including after deleting a shift_assignment row (which flips
 *   step 8 to incomplete), which locked admins out of the float view
 *   exactly when they were trying to exercise it. `is_dashboard_ready`
 *   is a narrower check: units + shift_configs + today's care_shifts.
 *   Backend falls back to is_setup_complete if the new flag is missing
 *   from the response (e.g. pre-upgrade). The wizard still auto-exits on
 *   is_setup_complete transitioning to true (no mid-flow disappearance)
 *   and the "Open Setup Guide" button still uses setupComplete so admins
 *   see it whenever any step remains.
 *
 * Changes (2026-04-18):
 * - minutesUntilStart now sourced from useShift's new dedicated
 *   state, not from shift.minutes_remaining (which is time-to-end).
 *   This fixes the "13h 49m" misreporting on upcoming-state dashboards.
 * - isAdmin derived from useAuth() memberships instead of a
 *   hardcoded `|| true` fallback. Resolves the TODO on that line.
 */
import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import useShift from '../../../hooks/useShift';
import useResidents from '../../../hooks/useResidents';
import { useAuth } from '../../auth/useAuth';
import ShiftSummaryBar from './ShiftSummaryBar';
import PendingCoSignBanner from './PendingCoSignBanner';
import FilterToolbar from './FilterToolbar';
import ResidentCard from './ResidentCard';
import ResidentDetail from './ResidentDetail';
import EmptyState from './EmptyState';
import SetupWizard from '../../admin/components/SetupWizard';
import GraceWindowBanner from '../../../components/shift/GraceWindowBanner';
import { COLORS } from '../../../config/constants';
import { getSetupStatus } from '../../../api/admin';
import ReopenEligibleBanner from './ReopenEligibleBanner';
import BedtimeCTABanner from './BedtimeCTABanner';

const ADMIN_ROLES = ['Care Admin'];

export default function ShiftDashboard() {
  const navigate = useNavigate();
  const { memberships } = useAuth();
  const role = memberships?.[0]?.role ?? '';
  const isAdmin = ADMIN_ROLES.includes(role);

  const [setupComplete, setSetupComplete] = useState(null); // null = loading, true/false
  const [showWizard, setShowWizard] = useState(false);

  // Preview mode: show residents before shift starts (read-only)
  const [previewMode, setPreviewMode] = useState(false);

  const {
    shift, shiftState, summary, handoffNotes,
    minutesUntilStart,
    inGraceWindow,
    loading: shiftLoading,
    refreshShift,
  } = useShift();

  // Convenient derived flag used in a few places below.
  const isFloatView = !!shift?.is_float_view;

  const residentsHook = useResidents(shift);
  const {
    residents, loading: residentsLoading,
    searchQuery, setSearchQuery,
    filters, toggleFilter, clearFilters, activeFilterCount,
    sortBy, setSortBy, viewMode, setViewMode,
    allResidents,
  } = residentsHook;

  const [selectedResident, setSelectedResident] = useState(null);
  const [summaryExpanded, setSummaryExpanded] = useState(true);

  // Reset preview mode if shift state changes (e.g. shift starts)
  useEffect(() => {
    if (shiftState !== 'upcoming') {
      setPreviewMode(false);
    }
  }, [shiftState]);

  // Navigate between residents in detail view
  const handlePrevResident = useCallback(() => {
    if (!selectedResident || residents.length === 0) return;
    const idx = residents.findIndex(r => r.patient_id === selectedResident.patient_id);
    if (idx > 0) setSelectedResident(residents[idx - 1]);
  }, [selectedResident, residents]);

  const handleNextResident = useCallback(() => {
    if (!selectedResident || residents.length === 0) return;
    const idx = residents.findIndex(r => r.patient_id === selectedResident.patient_id);
    if (idx < residents.length - 1) setSelectedResident(residents[idx + 1]);
  }, [selectedResident, residents]);

  const currentIndex = selectedResident
    ? residents.findIndex(r => r.patient_id === selectedResident.patient_id)
    : -1;

  // Check setup status (admin only).
  //
  // Auto-open the wizard only when the facility isn't yet dashboard-ready
  // (no units / no shift configs / no care_shifts today). Once those
  // minimums are in place, the admin can freely hit the dashboard even
  // if later wizard steps (residents, assignments, alerts) are incomplete.
  //
  // `setupComplete` tracks the old all-9-steps flag so:
  //   - the wizard's render guard (`showWizard && !setupComplete`) still
  //     keeps the wizard open mid-flow and auto-exits at 100%
  //   - the "Open Setup Guide" button in the no_shift empty state still
  //     appears whenever any step remains.
  useEffect(() => {
    if (!isAdmin) { setSetupComplete(true); return; }
    getSetupStatus()
      .then(data => {
        if (data.success) {
          setSetupComplete(data.is_setup_complete);
          // Fall back to is_setup_complete if backend hasn't been
          // upgraded with the new flag yet — preserves old behavior.
          const ready = data.is_dashboard_ready ?? data.is_setup_complete;
          if (!ready) setShowWizard(true);
        }
      })
      .catch(() => setSetupComplete(true)); // fail open
  }, [isAdmin]);

  // Show wizard if setup incomplete
  if (showWizard && !setupComplete) {
    return (
      <div style={{ paddingBottom: 80 }}>
        <SetupWizard onComplete={() => {
          setSetupComplete(true);
          setShowWizard(false);
          refreshShift();
        }} />
      </div>
    );
  }

  // Loading state
  if (shiftLoading) {
    return (
      <div style={{ padding: 40, textAlign: 'center' }}>
        <div style={{ fontSize: 32, marginBottom: 12 }}>⏳</div>
        <div style={{ color: COLORS.textMuted }}>Loading shift...</div>
      </div>
    );
  }

  // Edge states
  if (shiftState === 'no_shift') {
    return (
      <div>
        <EmptyState type="no_shift" />
        {isAdmin && setupComplete === false && (
          <div style={{ textAlign: 'center', marginTop: -20, paddingBottom: 80 }}>
            <button onClick={() => setShowWizard(true)} style={{
              padding: '12px 24px', borderRadius: 10, border: 'none',
              background: COLORS.primary, color: '#fff', fontSize: 14, fontWeight: 600, cursor: 'pointer',
            }}>
              Open Setup Guide
            </button>
          </div>
        )}
      </div>
    );
  }

  if (shiftState === 'upcoming' && !previewMode) {
    return (
      <EmptyState
        type="upcoming"
        minutesUntilStart={minutesUntilStart}
        onAction={() => setPreviewMode(true)}
      />
    );
  }

  if (shiftState === 'ended') {
    // Don't celebrate work that never happened. If the shift never activated
    // (status still 'scheduled' at end-of-grace), no assigned user logged in
    // during the window — render neutral copy in that case.
    const wasWorked = shift?.shift_status !== 'scheduled';
    return <EmptyState
      type={wasWorked ? 'ended' : 'ended_unworked'}
      summary={summary}
    />;
  }

  // Empty-residents state: pick the copy that matches the viewer.
  // Float-view admins see oversight-flavored copy; rostered CNAs see the
  // "your assignments are being finalized" copy.
  const emptyResidentsType = isFloatView ? 'no_residents_float' : 'no_residents';

  return (
    <div style={{ paddingBottom: 80 }}>

      {/* Grace window banner — shown when shift has ended but we're
          still within the 2-hour late-doc grace window. Per Policy
          v1.0 §4.3. Banner self-suppresses if shift is null. */}
      {inGraceWindow && <GraceWindowBanner shift={shift} />}

      {/* Preview mode banner */}
      {previewMode && shiftState === 'upcoming' && (
        <PreviewBanner
          minutesUntilStart={minutesUntilStart}
          onDismiss={() => setPreviewMode(false)}
        />
      )}

      {/* Shift Summary — hide in preview mode (no ADL data yet) */}
      {shiftState !== 'upcoming' && (
        <ShiftSummaryBar
          summary={summary}
          handoffNotes={handoffNotes}
          expanded={summaryExpanded}
          onToggle={() => setSummaryExpanded(!summaryExpanded)}
          onOverdueClick={() => {
            toggleFilter('status', 'overdue');
            setSummaryExpanded(false);
          }}
          isFloatView={isFloatView}
        />
      )}

      {/* Reopen-eligible records (time-sensitive — 24h cutoff) */}
      <ReopenEligibleBanner role={role} />

       {/* Supervisor co-sign queue */}
      <PendingCoSignBanner role={role} />

      {/* Bedtime checklist CTA */}
      <BedtimeCTABanner />
      
      {/* Filter/Search/Sort Toolbar */}
      <FilterToolbar
        searchQuery={searchQuery}
        onSearchChange={setSearchQuery}
        filters={filters}
        onToggleFilter={toggleFilter}
        onClearFilters={clearFilters}
        activeFilterCount={activeFilterCount}
        sortBy={sortBy}
        onSortChange={setSortBy}
        viewMode={viewMode}
        onViewModeChange={setViewMode}
      />

      {/* Resident List */}
      {residentsLoading && residents.length === 0 ? (
        <div style={{ padding: 40, textAlign: 'center', color: COLORS.textMuted }}>
          Loading residents...
        </div>
      ) : residents.length === 0 ? (
      <EmptyState
        type={allResidents?.length === 0 ? emptyResidentsType : 'no_results'}
        onClearFilters={clearFilters}
        onAction={allResidents?.length === 0 ? residentsHook.refresh : undefined}
      />
      ) : (
        <div style={{
          padding: '0 16px',
          display: viewMode === 'grid' ? 'grid' : 'flex',
          gridTemplateColumns: viewMode === 'grid' ? 'repeat(auto-fill, minmax(280px, 1fr))' : undefined,
          gap: viewMode === 'grid' ? 12 : 0,
          flexDirection: viewMode === 'list' ? 'column' : undefined,
        }}>
          {residents.map(resident => (
            <ResidentCard
              key={resident.patient_id}
              resident={resident}
              viewMode={viewMode}
              onTap={() => setSelectedResident(resident)}
              onStartADL={previewMode ? null : () => {
              navigate(`/dashboard/adl/${resident.patient_id}`, {
                state: {
                  patient_id: resident.patient_id,
                  first_name: resident.first_name,
                  last_name:  resident.last_name,
                },
              });
            }}
            />
          ))}
        </div>
      )}

      {/* Resident Detail (slide-in) */}
      {selectedResident && (
        <ResidentDetail
          patientId={selectedResident.patient_id}
          careShiftId={shift?.care_shift_id}
          onClose={() => setSelectedResident(null)}
          onPrev={currentIndex > 0 ? handlePrevResident : null}
          onNext={currentIndex < residents.length - 1 ? handleNextResident : null}
          currentIndex={currentIndex}
          totalCount={residents.length}
        />
      )}
    </div>
  );
}

/**
 * PreviewBanner
 * Shown at the top of the resident list when viewing in preview mode.
 * Communicates read-only state and time until shift start.
 */
function PreviewBanner({ minutesUntilStart, onDismiss }) {
  const hrs = Math.floor((minutesUntilStart ?? 0) / 60);
  const mins = (minutesUntilStart ?? 0) % 60;
  const timeStr = hrs > 0 ? `${hrs}h ${mins}m` : `${mins}m`;

  return (
    <div style={{
      margin: '12px 16px 4px',
      background: COLORS.primaryLight,
      border: `1px solid ${COLORS.primaryAlpha}`,
      borderRadius: 10,
      padding: '10px 14px',
      display: 'flex',
      alignItems: 'center',
      gap: 10,
    }}>
      <span style={{ fontSize: 18 }}>👀</span>
      <div style={{ flex: 1 }}>
        <div style={{ fontSize: 12, fontWeight: 700, color: COLORS.primary }}>
          PREVIEW MODE
        </div>
        <div style={{ fontSize: 12, color: COLORS.textMuted, marginTop: 1 }}>
          Shift starts in {timeStr}. ADL documentation will be available once your shift begins.
        </div>
      </div>
      <button
        onClick={onDismiss}
        style={{
          background: 'none', border: 'none', cursor: 'pointer',
          fontSize: 16, color: COLORS.textMuted, padding: '2px 6px',
          lineHeight: 1,
        }}
        aria-label="Dismiss preview"
      >
        ✕
      </button>
    </div>
  );
}